import React, { useState, useEffect } from 'react';
import {
  Users,
  Calendar,
  MapPin,
  Search,
  Filter,
  ChevronDown,
  Navigation,
  TrendingUp,
  Activity,
  Clock,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from 'recharts';
import groupTripAnalyticsService from '../service/groupTripAnalyticsService';

export default function GroupTrip() {
  // ✅ Calculate current month start and current date dynamically
  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    return {
      startDate: `${year}-${month}-01`, // First day of current month
      endDate: `${year}-${month}-${day}` // Today's date
    };
  };

  const { startDate: defaultStart, endDate: defaultEnd } = getCurrentMonthDates();

  const [selectedGroup, setSelectedGroup] = useState('');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [metrics, setMetrics] = useState(null);
  const [tripsByDay, setTripsByDay] = useState([]);
  const [distanceByDate, setDistanceByDate] = useState([]);
  const [groupSizeDistribution, setGroupSizeDistribution] = useState([]);
  const [topDestinations, setTopDestinations] = useState([]);
  const [recentTrips, setRecentTrips] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [allTrips, setAllTrips] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);

  // ✅ Inline Legend Component
  const InlineLegend = ({ items }) => (
    <div className="flex flex-wrap items-center gap-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200">
          <div
            className="w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-xs font-medium text-gray-700">{item.label}</span>
        </div>
      ))}
    </div>
  );

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('📊 Fetching group trip analytics for:', startDate, 'to', endDate);

      const result = await groupTripAnalyticsService.getAllAnalytics(
        startDate,
        endDate,
        selectedGroup || null
      );

      console.log('📦 Service Result:', result);

      if (result.success) {
        const { data } = result;
        console.log('✅ Data received:', data);

        setMetrics(data.metrics);
        setTripsByDay(data.tripsByDay || []);
        setDistanceByDate(data.distanceByDate || []);
        setGroupSizeDistribution(data.groupSizeDistribution || []);
        setTopDestinations(data.topDestinations || []);
        setRecentTrips(data.recentTrips || []);
        setAllTrips(data.recentTrips || []);
        setFilteredTrips(data.recentTrips || []);
        setGroups(data.groups || []);

        if (data.metrics?.statusDistribution) {
          const dist = data.metrics.statusDistribution;
          setStatusDistribution([
            { name: 'Completed', value: dist.completed || 0, color: '#10B981' },
            { name: 'Active', value: dist.active || 0, color: '#3B82F6' },
            { name: 'Upcoming', value: dist.upcoming || 0, color: '#8B5CF6' },
            { name: 'Cancelled', value: dist.cancelled || 0, color: '#EF4444' },
          ]);
        }

        console.log('✅ State updated successfully');
      } else {
        console.error('❌ Service error:', result.message);
        setError(result.message);
        
        setMetrics(null);
        setTripsByDay([]);
        setDistanceByDate([]);
        setGroupSizeDistribution([]);
        setTopDestinations([]);
        setRecentTrips([]);
        setFilteredTrips([]);
        setAllTrips([]);
        setGroups([]);
        setStatusDistribution([]);
      }
    } catch (err) {
      console.error('❌ Fetch Error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }
    fetchAnalyticsData();
  };

  const handleReset = () => {
    const { startDate: defaultStart, endDate: defaultEnd } = getCurrentMonthDates();
    setSelectedGroup('');
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setFilteredTrips(allTrips);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-emerald-100 text-emerald-700';
      case 'Active':
        return 'bg-blue-100 text-blue-700';
      case 'Upcoming':
        return 'bg-purple-100 text-purple-700';
      case 'Cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-8 h-8 text-indigo-600" />
            <span className="text-sm font-semibold text-emerald-600">Total</span>
          </div>
          <p className="text-sm text-gray-600">Group Trips</p>
          <p className="text-2xl font-bold text-gray-900">
            {metrics?.totalTrips || 0}
          </p>
        </div>

        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <span className="text-sm font-semibold text-blue-600">Active</span>
          </div>
          <p className="text-sm text-gray-600">Active Groups</p>
          <p className="text-2xl font-bold text-gray-900">
            {metrics?.activeTrips || 0}
          </p>
        </div>

        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-600">People</span>
          </div>
          <p className="text-sm text-gray-600">Total Members</p>
          <p className="text-2xl font-bold text-gray-900">
            {metrics?.totalMembers || 0}
          </p>
        </div>

        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <Navigation className="w-8 h-8 text-purple-600" />
            <span className="text-sm font-semibold text-purple-600">KM</span>
          </div>
          <p className="text-sm text-gray-600">Total Distance</p>
          <p className="text-2xl font-bold text-gray-900">
            {parseFloat(metrics?.totalDistance || 0).toLocaleString()} km
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h4 className="text-lg font-bold text-gray-900">Filter Group Trips</h4>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Group
            </label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full appearance-none pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white cursor-pointer"
              >
                <option value="">All Groups</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex space-x-3 mt-4">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                <span>Search Filter</span>
              </>
            )}
          </button>
          <button onClick={handleReset} className="btn-secondary">
            Reset
          </button>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing data from{' '}
          <span className="font-semibold">{new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span> to{' '}
          <span className="font-semibold">{new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ✅ Bar Chart - Daily Trip Status with Inline Legend */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              Daily Group Trip Status
            </h4>
            {tripsByDay.length > 0 && (
              <InlineLegend
                items={[
                  { color: '#10B981', label: 'Completed' },
                  { color: '#3B82F6', label: 'Active' },
                  { color: '#EF4444', label: 'Cancelled' }
                ]}
              />
            )}
          </div>
          {tripsByDay.length > 0 ? (
            <div className="overflow-x-auto">
              <div style={{ minWidth: `${Math.max(tripsByDay.length * 50, 600)}px`, width: '100%' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tripsByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="day" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="completed" fill="#10B981" name="Completed" />
                    <Bar dataKey="active" fill="#3B82F6" name="Active" />
                    <Bar dataKey="cancelled" fill="#EF4444" name="Cancelled" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>

        {/* ✅ Pie Chart - Status Distribution with Inline Legend */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              Trip Status Distribution
            </h4>
            {statusDistribution.some((item) => item.value > 0) && (
              <InlineLegend
                items={statusDistribution.map(item => ({
                  color: item.color,
                  label: item.name
                }))}
              />
            )}
          </div>
          {statusDistribution.some((item) => item.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>

        {/* ✅ Combo Chart - Trips & Members with Inline Legend */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              Daily Distance & Members
            </h4>
            {distanceByDate.length > 0 && (
              <InlineLegend
                items={[
                  { color: '#4F46E5', label: 'Distance (km)' },
                  { color: '#10B981', label: 'Members' }
                ]}
              />
            )}
          </div>
          {distanceByDate.length > 0 ? (
            <div className="overflow-x-auto">
              <div style={{ minWidth: `${Math.max(distanceByDate.length * 50, 600)}px`, width: '100%' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={distanceByDate}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="day"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="distance" fill="#4F46E5" name="Distance (km)" />
                    <Line
                      type="monotone"
                      dataKey="members"
                      stroke="#10B981"
                      strokeWidth={3}
                      name="Members"
                      dot={{ r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>

        {/* ✅ Bar Chart - Group Size Distribution with Inline Legend */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              Group Size Distribution
            </h4>
            {groupSizeDistribution.length > 0 && (
              <InlineLegend
                items={[
                  { color: '#8B5CF6', label: 'Number of Groups' }
                ]}
              />
            )}
          </div>
          {groupSizeDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={groupSizeDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="size" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8B5CF6" name="Number of Groups" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>

        {/* ✅ Bar Chart - Top Destinations with Inline Legend */}
        <div className="glass rounded-2xl p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">
              Top Destinations
            </h4>
            {topDestinations.length > 0 && (
              <InlineLegend
                items={[
                  { color: '#10B981', label: 'Groups' },
                  { color: '#3B82F6', label: 'Members' }
                ]}
              />
            )}
          </div>
          {topDestinations.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topDestinations} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="destination" 
                  type="category" 
                  width={150}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Bar dataKey="groups" fill="#10B981" name="Groups" />
                <Bar dataKey="members" fill="#3B82F6" name="Members" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>
      </div>

      {/* Group Trips Table */}
      <div className="glass rounded-2xl p-6">
        <h4 className="text-lg font-bold text-gray-900 mb-4">
          All Group Trips ({filteredTrips.length})
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Group Name
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Destination
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Duration
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Distance
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Members
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Dates
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading trips...
                  </td>
                </tr>
              ) : filteredTrips.length > 0 ? (
                filteredTrips.map((trip, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-900 font-medium">
                          {trip.groupName}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-600">{trip.destination}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-600">{trip.duration}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600">{trip.distance}</td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
                        {trip.members} members
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-600 text-sm">
                      {trip.startDate} - {trip.endDate}
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                          trip.status
                        )}`}
                      >
                        {trip.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="py-8 text-center text-gray-500">
                    No group trips found. Try adjusting your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
